import { Button, Input, message, Modal, Select } from "antd";
import React, { CSSProperties, useEffect, useState } from "react";
import {
  useProductTypeList,
  useGetProductById,
  useCreateProduct,
  useUpdateProduct,
} from "../../api/product";
import { UnitOfMeasurementSelect } from "../../components/form/UnitOfMeasurementSelect";
import { DeleteOutlined } from "@ant-design/icons";
import { OutputFormValues, outputSchema } from "../../schemas/formSchema";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

type ProductModalProps = {
  open: boolean;
  selectedId: string;
  hideModal: () => void;
};

interface ITag {
  key: string;
  value: string;
}

const wrapper: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
  marginTop: 25,
};

const label: CSSProperties = {
  fontFamily: "Poppins",
  fontSize: 15,
};
const AdminProductModal: React.FC<ProductModalProps> = ({
  open,
  selectedId,
  hideModal,
}) => {
  const [tags, setTags] = useState<ITag[]>([]);
  const [tagKey, setTagKey] = useState<string>();
  const [tagValue, setTagValue] = useState<string>();

  const { data: productTypes } = useProductTypeList();
  const { data: selectedProduct } = useGetProductById(selectedId);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<OutputFormValues>({
    resolver: zodResolver(outputSchema) as any,
    defaultValues: {
      productType: undefined,
      name: "",
      unitOfMeasurementId: undefined,
      description: "",
      tagKey: "",
      variety: "",
    },
  });

  const { mutateAsync: createProduct, isPending: isCreatingProduct } =
    useCreateProduct();
  const { mutateAsync: updateProduct, isPending: isUpdatingProduct } =
    useUpdateProduct();

  useEffect(() => {
    if (selectedProduct && selectedProduct.data && selectedProduct.data.data) {
      const productData = selectedProduct.data.data;

      reset({
        productType: productData.category || "",
        name: productData.productName || "",
        variety: productData.variety || "",
        unitOfMeasurementId: productData.unitOfMeasurementId || undefined,
        description: productData.description || "",
      });

      if (productData.detail) {
        try {
          const val = Object.entries(JSON.parse(productData.detail)).map(
            (m: any) => ({
              key: m[0],
              value: m[1],
            })
          );
          setTags(val);
        } catch (err) {
          console.error("Error parsing detail JSON:", err);
          setTags([]);
        }
      } else {
        setTags([]);
      }
    } else {
      reset({
        productType: "",
        name: "",
        variety: "",
        unitOfMeasurementId: undefined,
        description: "",
      });
      setTags([]);
    }
  }, [selectedProduct, reset, open]);

  const onSubmit = async (data: OutputFormValues) => {
    if (!data) return;
    const _tags = tags.reduce((acc, tag) => {
      return { ...acc, ...(tag.key ? { [tag.key]: tag.value } : tag) };
    }, {});

    const outputData = {
      id: selectedId,
      product: {
        category: data.productType,
        productName: data.name,
        variety: data.variety,
        detail: JSON.stringify(_tags),
        description: data.description as any,
        unitOfMeasurementId: data.unitOfMeasurementId,
      },
    };

    if (selectedId) {
      try {
        const result = await updateProduct(outputData);

        if (result && result) {
          message.success("Output product updated successfully");
          reset();
        }
      } catch (err: any) {
        message.error("Operation failed");
      }
    } else {
      try {
        const result = await createProduct({
          category: data.productType,
          productName: data.name,
          variety: data.variety,
          detail: JSON.stringify(_tags),
          description: data.description,
          unitOfMeasurementId: data.unitOfMeasurementId,
        });
        if (result && result.status === 200) {
          message.success("Output product created successfully");
          reset();
        }
      } catch (err: any) {
        message.error("Operation failed");
      }
    }

    hideModal();
  };

  const handleInsertTag = () => {
    if (tagKey && tagValue) {
      const _tag = tags.find((tag) => tag.key === tagKey);

      if (!_tag) {
        setTags((prev) => [...prev, { key: tagKey, value: tagValue }]);
        setTagKey(undefined);
        setTagValue(undefined);
      }
    }
  };

  const handleRemoveTag = (key: string) => {
    if (key) {
      const _tags = tags.filter((tag) => tag.key !== key);
      setTags(_tags);
    }
  };

  const { TextArea } = Input;
  return (
    <Modal
      width={"60%"}
      title={
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 600,
            fontSize: 27.43,
            color: "black",
          }}
        >
          {selectedId ? "Edit output product" : "Create output product"}
        </div>
      }
      open={open}
      centered
      onOk={handleSubmit(onSubmit as any)}
      onCancel={hideModal}
      closable
      footer={
        <div className="flex justify-end gap-3 p-4">
          <Button
            onClick={hideModal}
            className="!border-gray-300 !text-gray-700 font-medium hover:!bg-gray-100 hover:!text-gray-900 transition-all duration-200 bg-transparent"
          >
            Cancel
          </Button>

          <Button
            type="primary"
            onClick={handleSubmit(onSubmit as any)}
            className="!bg-[#009602] !border-none hover:!bg-[#00b306] font-medium transition-all duration-200"
            loading={isCreatingProduct || isUpdatingProduct}
          >
            {selectedId ? "Update" : "Create"}
          </Button>
        </div>
      }
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          color: "black",
          gap: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            width: "49%",
          }}
        >
          {productTypes &&
            productTypes.data &&
            productTypes.data.length > 0 && (
              <Controller
                name="productType"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder="Select product type"
                    style={{ width: "100%" }}
                    value={field.value || undefined}
                    options={productTypes?.data.map((type: string) => {
                      return { label: type, value: type };
                    })}
                  />
                )}
              />
            )}

          {errors.productType && (
            <div className="text-red-400">{errors.productType.message}</div>
          )}

          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Input {...field} placeholder="Insert name" />
            )}
          />

          {errors.name && (
            <div className="text-red-400">{errors.name.message}</div>
          )}
          <Controller
            name="variety"
            control={control}
            render={({ field }) => (
              <Input {...field} placeholder="Insert variety" />
            )}
          />

          {errors.variety && (
            <div className="text-red-400">{errors.variety.message}</div>
          )}

          <Controller
            name="unitOfMeasurementId"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unit of measurement *
                </label>
                <UnitOfMeasurementSelect
                  value={field.value}
                  onChange={field.onChange}
                  status={errors.unitOfMeasurementId ? "error" : undefined}
                />
              </div>
            )}
          />
          {errors.unitOfMeasurementId && (
            <div className="text-red-400">
              {errors.unitOfMeasurementId.message}
            </div>
          )}

          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextArea rows={4} placeholder="Description" {...field} />
            )}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            marginTop: 5,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 4,
            }}
          >
            <svg
              width="30px"
              height="30px"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.0498 7.0498H7.0598M10.5118 3H7.8C6.11984 3 5.27976 3 4.63803 3.32698C4.07354 3.6146 3.6146 4.07354 3.32698 4.63803C3 5.27976 3 6.11984 3 7.8V10.5118C3 11.2455 3 11.6124 3.08289 11.9577C3.15638 12.2638 3.27759 12.5564 3.44208 12.8249C3.6276 13.1276 3.88703 13.387 4.40589 13.9059L9.10589 18.6059C10.2939 19.7939 10.888 20.388 11.5729 20.6105C12.1755 20.8063 12.8245 20.8063 13.4271 20.6105C14.112 20.388 14.7061 19.7939 15.8941 18.6059L18.6059 15.8941C19.7939 14.7061 20.388 14.112 20.6105 13.4271C20.8063 12.8245 20.8063 12.1755 20.6105 11.5729C20.388 10.888 19.7939 10.2939 18.6059 9.10589L13.9059 4.40589C13.387 3.88703 13.1276 3.6276 12.8249 3.44208C12.5564 3.27759 12.2638 3.15638 11.9577 3.08289C11.6124 3 11.2455 3 10.5118 3ZM7.5498 7.0498C7.5498 7.32595 7.32595 7.5498 7.0498 7.5498C6.77366 7.5498 6.5498 7.32595 6.5498 7.0498C6.5498 6.77366 6.77366 6.5498 7.0498 6.5498C7.32595 6.5498 7.5498 6.77366 7.5498 7.0498Z"
                stroke="#009602"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div style={{ fontFamily: "Poppins", fontSize: 15 }}>Tags</div>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              border: "1px solid #E6EEF6",
              borderRadius: 4,
              gap: 12,
              paddingLeft: 50,
              paddingRight: 50,
              paddingTop: 0,
              paddingBottom: 20,
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                marginTop: -10,
              }}
            >
              {tags &&
                tags.map((tag) => (
                  <div
                    key={tag.key}
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      borderBottom: "1px solid #DCE2E4",
                      paddingTop: 20,
                      paddingBottom: 5,
                    }}
                  >
                    <div>{Object.values(tag)[0]}</div>
                    <div>
                      {tag.value}{" "}
                      <DeleteOutlined
                        style={{ color: "red" }}
                        onClick={() => handleRemoveTag(tag.key)}
                      />
                    </div>
                  </div>
                ))}
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: 12,
                width: "100%",
              }}
            >
              <div style={{ ...wrapper, width: "50%", marginTop: 15 }}>
                <div style={label}>Key</div>
                <Input
                  value={tagKey}
                  onChange={(e) => setTagKey(e.target.value)}
                  placeholder="Insert key"
                />
              </div>

              <div style={{ ...wrapper, width: "50%", marginTop: 15 }}>
                <div style={label}>Value</div>
                <Input
                  value={tagValue}
                  onChange={(e) => setTagValue(e.target.value)}
                  placeholder="Insert value"
                />
              </div>
            </div>
            <Button style={{ width: 150 }} onClick={handleInsertTag}>
              Add new tag
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AdminProductModal;
